import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Users, Plus, Edit2, Trash2, X, AlertCircle } from 'lucide-react';
import { getRegions, getRegion, createRegion, updateRegion, deleteRegion, getVehicles } from '../api';
import { useAuth } from '../AuthContext';

const emptyForm = {
  name: '',
  coordinator: '',
  phone: '',
  address: '',
  email: '',
};

export default function Regions() {
  const { hasMenuAction } = useAuth();
  const [regions, setRegions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadData = () => {
    setLoading(true);
    Promise.all([getRegions(), getVehicles({ limit: 1000 })])
      .then(([regRes, vehRes]) => {
        setRegions(regRes.data);
        setVehicles(vehRes.data?.data || []);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const getVehicleCount = (regionName) =>
    vehicles.filter(v => v.region_name === regionName).length;

  const openCreate = () => {
    setEditingRegion(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = async (region) => {
    setEditingRegion(region);
    setError('');
    try {
      const res = await getRegion(region.id);
      const r = res.data;
      setForm({
        name: r.name || '',
        coordinator: r.coordinator || '',
        phone: r.phone || '',
        address: r.address || '',
        email: r.email || '',
      });
      setShowModal(true);
    } catch (err) {
      setError('Gagal memuat data wilayah');
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.name) { setError('Nama wilayah harus diisi'); return; }

    setSaving(true);
    try {
      if (editingRegion) {
        await updateRegion(editingRegion.id, form);
      } else {
        await createRegion(form);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan wilayah');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (region) => {
    const count = getVehicleCount(region.name);
    const msg = count > 0
      ? `Yakin ingin menghapus "${region.name}"? ${count} kendaraan terdaftar di wilayah ini akan kehilangan referensi wilayah.`
      : `Yakin ingin menghapus "${region.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteRegion(region.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus wilayah');
    }
  };

  if (loading) return <div className="page-loading">Memuat data wilayah...</div>;

  return (
    <div className="page regions-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Wilayah Operasional</h2>
            <p className="page-desc">{regions.length} wilayah KWK terdaftar</p>
          </div>
          {hasMenuAction('regions', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} />
              <span>Tambah Wilayah</span>
            </button>
          )}
        </div>
      </div>

      <div className="regions-grid">
        {regions.map((region) => (
          <div key={region.id} className="region-card">
            <div className="region-card-header">
              <MapPin size={24} className="region-icon" />
              <h3>{region.name}</h3>
              {(hasMenuAction('regions', 'edit') || hasMenuAction('regions', 'delete')) && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  {hasMenuAction('regions', 'edit') && (
                    <button className="btn-icon" style={{ color: 'rgba(255,255,255,0.7)' }} title="Edit" onClick={() => openEdit(region)}>
                      <Edit2 size={14} />
                    </button>
                  )}
                  {hasMenuAction('regions', 'delete') && (
                    <button className="btn-icon" style={{ color: 'rgba(255,255,255,0.7)' }} title="Hapus" onClick={() => handleDelete(region)}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="region-card-body">
              <div className="region-stat">
                <Users size={16} />
                <span>{getVehicleCount(region.name)} Kendaraan</span>
              </div>
              <div className="region-detail">
                <span>Ka. Ukerwil</span>
                <strong>{region.coordinator || '-'}</strong>
              </div>
              <div className="region-detail">
                <Phone size={14} />
                <span>{region.phone || '-'}</span>
              </div>
              <div className="region-detail">
                <Mail size={14} />
                <span>{region.email || '-'}</span>
              </div>
              <p className="region-address">{region.address || '-'}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <MapPin size={18} style={{ marginRight: 8 }} />
                {editingRegion ? 'Edit Wilayah' : 'Tambah Wilayah Baru'}
              </h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="form-error">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Nama Wilayah *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Contoh: KWK UTARA"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Ka. Ukerwil</label>
                  <input
                    type="text"
                    value={form.coordinator}
                    onChange={e => setForm({ ...form, coordinator: e.target.value })}
                    placeholder="Nama coordinator"
                  />
                </div>
                <div className="form-group">
                  <label>No. Telepon</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="Nomor telepon"
                  />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Alamat</label>
                  <textarea
                    value={form.address}
                    onChange={e => setForm({ ...form, address: e.target.value })}
                    placeholder="Alamat wilayah"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
